import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import GoogleDocsPicker from '../../components/GoogleDocsPicker';
import GmailPanel from '../../components/GmailPanel';
import EssayDetailView, { ESSAY_DOC_ROLES } from './EssayDetailView';
import { DeskLink, essayTitle } from './essayShared';
import { describeShareResults, shareDocWithAudience } from '../../lib/googleDriveShare';

// Ask Google for a Drive access token from an already-loaded picker session.
// The picker component initialises a token client on mount, so once the user
// has picked once we can reuse that flow to hand out a fresh token for a
// re-share. Returns a promise for the token string.
const requestDriveToken = () =>
  new Promise((resolve, reject) => {
    if (!window.google?.accounts?.oauth2) {
      reject(new Error('Google auth not loaded'));
      return;
    }
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      scope: 'https://www.googleapis.com/auth/drive.file',
      callback: (response) => {
        if (response.access_token) resolve(response.access_token);
        else reject(new Error(response.error || 'No token'));
      },
    });
    client.requestAccessToken({ prompt: '' });
  });

// The student's own view of one essay: everything on, nothing hidden.
export default function CollegeEssayDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();

  const [essay, setEssay] = useState(null);
  const [docs, setDocs] = useState([]);
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEmail, setShowEmail] = useState(false);
  const composerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get(`/essays/${id}`),
      api.get(`/essay-docs/${id}`).catch(() => ({ data: { docs: [] } })),
      api.get('/college-access').catch(() => ({ data: { grants: [] } })),
    ])
      .then(([essayRes, docsRes, accessRes]) => {
        if (!alive) return;
        setEssay(essayRes.data.essay);
        setDocs(docsRes.data.docs || []);
        setGrants((accessRes.data.grants || []).filter((g) => g.status !== 'REVOKED'));
      })
      .catch(() => {
        if (!alive) return;
        toast.error('Failed to load that essay');
        navigate('/college/essays');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [api, id, navigate]);

  const patchEssay = async (payload, { quiet } = {}) => {
    try {
      const res = await api.patch(`/essays/${id}`, payload);
      setEssay((prev) => ({ ...prev, ...res.data.essay }));
      if (!quiet) toast.success('Saved');
      return res.data.essay;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
      throw err;
    }
  };

  // Say so when an email actually went out, and say so when it didn't.
  const announce = (delivery) => {
    if (delivery?.sent) toast.success(`Emailed ${delivery.to.length === 1 ? '1 person' : `${delivery.to.length} people`}`);
    else if (delivery?.error) toast.error("Saved, but the email didn't send");
  };

  const viewer = useMemo(
    () => ({ kind: 'user', id: user?.id, name: user?.name, role: 'STUDENT' }),
    [user]
  );

  // The student first, then everyone they let in.
  const people = useMemo(
    () => [
      { kind: 'user', id: user?.id, name: user?.name, role: 'STUDENT', isYou: true },
      ...grants.map((g) => ({ ...g, kind: 'grant' })),
    ],
    [grants, user]
  );

  const actions = {
    patchEssay,
    composerRef,
    postNote: async (body, parentId = null, { notify } = {}) => {
      const res = await api.post(`/essays/${id}/notes`, { body, parentId, notify: notify === true });
      setEssay((prev) => ({ ...prev, brainstormNotes: [...prev.brainstormNotes, res.data.note] }));
      announce(res.data.delivery);
    },
    starNote: async (noteId, starred) => {
      const res = await api.patch(`/essays/${id}/notes/${noteId}`, { starred });
      setEssay((prev) => ({
        ...prev,
        brainstormNotes: prev.brainstormNotes.map((n) =>
          n.id === res.data.note.id ? res.data.note : starred ? { ...n, starred: false } : n
        ),
      }));
    },
    noteToTask: async (note) => {
      const res = await api.post(`/essays/${id}/tasks`, {
        title: note.body.length > 90 ? `${note.body.slice(0, 90)}…` : note.body,
        detail: `From ${note.author?.name || 'a brainstorm note'}`,
        assignees: [{ kind: 'user', id: user.id }],
      });
      setEssay((prev) => ({ ...prev, tasks: [...prev.tasks, res.data.task] }));
      toast.success('Added to tasks');
    },
    addTask: async (payload) => {
      const res = await api.post(`/essays/${id}/tasks`, payload);
      setEssay((prev) => ({ ...prev, tasks: [...prev.tasks, res.data.task] }));
      announce(res.data.delivery);
    },
    setTaskStatus: async (task, status) => {
      const res = await api.patch(`/essays/${id}/tasks/${task.id}`, { status });
      setEssay((prev) => ({ ...prev, tasks: prev.tasks.map((t) => (t.id === task.id ? res.data.task : t)) }));
    },
    deleteTask: async (task) => {
      await api.delete(`/essays/${id}/tasks/${task.id}`);
      setEssay((prev) => ({ ...prev, tasks: prev.tasks.filter((t) => t.id !== task.id) }));
    },
    toggleChecklist: async (item) => {
      try {
        await api.patch(`/essays/${id}/checklist/${item.id}`, { completed: !item.completed });
        setEssay((prev) => ({
          ...prev,
          checklistItems: prev.checklistItems.map((i) =>
            i.id === item.id ? { ...i, completed: !item.completed } : i
          ),
        }));
      } catch {
        toast.error('Failed to update your checklist');
      }
    },
    setCurrentDoc: async (docId) => {
      try {
        await api.patch(`/essay-docs/${id}/${docId}`, { isCurrent: true });
        setDocs((prev) => prev.map((d) => ({ ...d, isCurrent: d.id === docId })));
      } catch {
        toast.error('Failed to set the current draft');
      }
    },
    reshareDoc: (doc) => reshareDoc(doc),
    goShare: () => navigate('/college/access'),
  };

  const attachDoc = async (docData) => {
    try {
      const res = await api.post(`/essay-docs/${id}`, {
        url: docData.url,
        title: docData.title,
        role: docData.role || 'DRAFT',
        isCurrent: docData.isCurrent || docs.length === 0,
        googleDocId: docData.googleDocId || null,
      });
      const doc = res.data.doc;
      setDocs((prev) => [doc, ...(doc.isCurrent ? prev.map((d) => ({ ...d, isCurrent: false })) : prev)]);

      // Auto-share with everyone who has drafts access. The Drive call is made
      // from the browser using the student's own OAuth token, so the app never
      // holds Drive credentials of its own. The Google-side share email is
      // gated by the same rule as the rest of the app: nothing goes out
      // unless the student ticked notify on the attach step.
      if (doc.googleDocId && docData.accessToken) {
        try {
          const audienceRes = await api.get(`/essays/${id}/draft-audience`);
          const audience = audienceRes.data.audience || [];
          if (audience.length > 0) {
            const { results = [], skipped } = await shareDocWithAudience({
              fileId: doc.googleDocId,
              accessToken: docData.accessToken,
              audience,
              sendNotificationEmail: docData.notify === true,
            });
            if (results.length > 0) {
              await api.post(`/essay-docs/${id}/${doc.id}/shares`, { results }).catch(() => {});
              setDocs((prev) => prev.map((d) => (d.id === doc.id ? { ...d, sharedWith: appendLog(d.sharedWith, results) } : d)));
              const summary = describeShareResults(results);
              if (summary) {
                const good = results.filter((r) => r.ok).length;
                (good > 0 ? toast.success : toast.error)(summary);
              }
            } else if (skipped) {
              // Nothing to share with — that's fine, no toast needed.
            }
          }
        } catch (shareErr) {
          toast.error("Attached, but the draft couldn't be shared with editors — try Share now on the doc");
        }
      }
    } catch {
      toast.error('Failed to attach that document');
    }
  };

  // A doc's sharedWith log is a JSON array on the server — the browser copy
  // keeps the same shape so a fresh page load and an in-memory update read the
  // same.
  const appendLog = (existing, entries) => {
    const now = new Date().toISOString();
    return [
      ...(Array.isArray(existing) ? existing : []),
      ...entries.map((r) => ({
        email: r.email,
        name: r.name || null,
        role: r.role || 'writer',
        at: now,
        ok: r.ok === true,
        error: r.ok === true ? null : r.error || 'unknown',
      })),
    ];
  };

  // Called from a per-doc "Share now" action when a student wants to bring
  // editor access back into line — after adding a new parent, say, or after a
  // previous share failed.
  const reshareDoc = async (doc) => {
    if (!doc.googleDocId) {
      toast.error("This doc doesn't have a Drive file id on it — can't share it automatically");
      return;
    }
    let accessToken;
    try {
      accessToken = await requestDriveToken();
    } catch (err) {
      toast.error("Couldn't get Google permission — try again");
      return;
    }
    try {
      const audienceRes = await api.get(`/essays/${id}/draft-audience`);
      const audience = audienceRes.data.audience || [];
      if (audience.length === 0) {
        toast.message('Nobody has drafts access yet');
        return;
      }
      const { results = [] } = await shareDocWithAudience({
        fileId: doc.googleDocId,
        accessToken,
        audience,
        sendNotificationEmail: false,
      });
      if (results.length > 0) {
        await api.post(`/essay-docs/${id}/${doc.id}/shares`, { results }).catch(() => {});
        setDocs((prev) => prev.map((d) => (d.id === doc.id ? { ...d, sharedWith: appendLog(d.sharedWith, results) } : d)));
        const summary = describeShareResults(results);
        const good = results.filter((r) => r.ok).length;
        if (summary) (good > 0 ? toast.success : toast.error)(summary);
      }
    } catch {
      toast.error("Couldn't run the share");
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '44px 32px 64px' }}>
        <p className="font-mono-editorial" style={{ fontSize: 13, color: 'var(--college-muted)' }}>
          Loading essay…
        </p>
      </div>
    );
  }
  if (!essay) return null;

  const school = essay.collegeApplication?.collegeName || essay.school;

  return (
    <EssayDetailView
      essay={essay}
      docs={docs}
      people={people}
      viewer={viewer}
      can={{
        viewerIsOwner: true,
        editDetails: true,
        changeStage: true,
        markReviewed: true,
        manageAccess: true,
        manageAnyTask: true,
        addTask: true,
        assignOthers: true,
        comment: true,
        star: true,
        seeChecklist: true,
        attachDocs: true,
      }}
      actions={actions}
      breadcrumb={[
        { label: 'Essays', to: '/college/essays' },
        ...(school
          ? [{ label: school, to: essay.collegeApplication ? `/college/${essay.collegeApplication.id}` : undefined }]
          : []),
      ]}
      docPickerSlot={
        <GoogleDocsPicker
          onDocSelected={attachDoc}
          projectId={id}
          docRoles={ESSAY_DOC_ROLES}
          buttonText="+ Attach a Google Doc"
          buttonVariant="ghost"
          buttonSize="sm"
        />
      }
      emailSlot={
        /* The Gmail thread filters this essay already carried. Tucked away so
           they don't compete with the desk, but not dropped. */
        <div>
          <DeskLink onClick={() => setShowEmail((v) => !v)} style={{ fontSize: 12 }}>
            {showEmail ? 'Hide email threads ↑' : 'Email threads ↓'}
          </DeskLink>
          {showEmail && (
            <div style={{ marginTop: 12 }}>
              <GmailPanel
                supervisorEmails={essay.supervisorEmails || []}
                emailSubjectFilters={essay.emailSubjectFilters || []}
                contextTitle={essayTitle(essay)}
                onUpdateFilters={async ({ supervisorEmails, emailSubjectFilters }) => {
                  try {
                    await api.patch(`/essays/${id}`, { supervisorEmails, emailSubjectFilters });
                    setEssay((prev) => ({ ...prev, supervisorEmails, emailSubjectFilters }));
                  } catch {
                    toast.error('Failed to save email filters');
                  }
                }}
              />
            </div>
          )}
        </div>
      }
    />
  );
}
